import React, { useState, useRef, useEffect } from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import sortBy from "lodash/sortBy";
import AddProductForm from "../forms/AddProductForm";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DraggableProductRow } from "./productRow";
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { toast } from "react-toastify";
import { materialItemsHeaders, materialItemsHeadersEditable } from "../../utils";
import { api } from "store/slices/api";
import "../../style.scss";

const QuoteFloorProducts = () => {
  const tableBodyNode = useRef();
  const navigate = useNavigate();
  const { quoteId, floorId } = useParams();
  const [showAddProduct, setShowAddProduct] = useState(false);
  const { data: quoteFields = {} } = api.useGetPriceQuoteQuery(quoteId);
  const { data: products = [] } = api.useGetFloorProductsQuery(floorId);
  const [updateFloorProducts] = api.useUpdateFloorProductsMutation();
  const [checkIfUserCanUpdatePrc] = api.useLazyGetCanUserUpdatePrcQuery();
  const { storeCcn, terrCcn, fcRole, sub, hasUpdateAccess } = useSelector((state) => state.loggedInUser);

  // pre-loading favorite products so that they are immediately available when user opens the add product form
  api.useGetFavoriteProductsQuery(sub);

  const altPShortcutListener = (e) => {
    if (e.altKey && e.code === "KeyP") {
      setShowAddProduct((showing) => !showing);
      e.preventDefault();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", altPShortcutListener);
    return () => {
      window.removeEventListener("keydown", altPShortcutListener);
    };
  }, []);

  const canEdit =
    (quoteFields.territory === terrCcn && quoteFields.territory !== "800100") ||
    (storeCcn === quoteFields.storeNumber && quoteFields.punchoutPublished !== true);

  const handleNavigatePrc = async () => {
    const body = { userRole: fcRole, acctNbr: quoteFields?.accountNumber, terrCcn, storeCcn };
    const canUserUpdatePrc = (await checkIfUserCanUpdatePrc(body)).data;
    if (canUserUpdatePrc === "FALSE" || canUserUpdatePrc === "DEPT_30_ONLY") {
      toast.error(
        "You cannot update the PRC for this account because your home store and territory do not match those on the account.",
      );
    } else {
      navigate(`/priceQuote/${quoteId}/updatePrc`);
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    }),
  );

  const headers = canEdit ? materialItemsHeadersEditable : materialItemsHeaders;
  const isSelectAllCheckedProducts = products.length > 0 && products.every((p) => p.isTaxChecked);
  const handleSelectAllProducts = () => {
    const taxRate = parseFloat(quoteFields.taxRate ?? 0) / 100;
    toast.promise(
      Promise.all(
        products.map((item) => {
          const extendedPriceInCents = Math.round(parseFloat(item.extendedPrice) * 100);
          const salesTaxInCents = !isSelectAllCheckedProducts ? Math.round(extendedPriceInCents * taxRate) : 0;
          const totalInCents = extendedPriceInCents + salesTaxInCents;
          return updateFloorProducts({
            productId: item.productId,
            floorId,
            isTaxChecked: !isSelectAllCheckedProducts,
            salesTax: salesTaxInCents / 100,
            total: totalInCents / 100,
            isEdited: true,
          }).unwrap();
        }),
      ),
      { pending: "Saving changes", success: "Products updated successfully", error: "Failed to update products" },
    );
  };

  const sorted = sortBy(products, ["rank", "productName"]).map((item, index) => ({
    ...item,
    rank: item.rank ?? index + 1,
  }));

  return (
    <>
      <div className="d-flex justify-content-between align-items-center border-top pt-5 pb-2">
        <div className="fw-bold">Material Items</div>
        {canEdit && (
          <div>
            {hasUpdateAccess && (
              <Button variant="outline-primary" onClick={handleNavigatePrc}>
                Update PRC
              </Button>
            )}
            <Button variant="success" className="m-0" onClick={() => setShowAddProduct(true)}>
              Add Products
            </Button>
          </div>
        )}
      </div>
      {showAddProduct && <AddProductForm handleProductLookup={setShowAddProduct} />}
      <div className="mb-5 container-fluid search-table">
        <div className="d-block d-md-none table-responsive customer-search__table">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => {
              const { active, over } = event;
              if (!over) return;
              const oldIndex = sorted.findIndex((item) => item.rank === active.id);
              const newIndex = sorted.findIndex((item) => item.rank === over.id);
              if (oldIndex !== newIndex) {
                toast.promise(
                  Promise.all(
                    arrayMove(sorted, oldIndex, newIndex).map((item, index) =>
                      updateFloorProducts({ productId: item.productId, floorId, rank: index + 1 }).unwrap(),
                    ),
                  ),
                  { pending: "Saving order", success: "Product order saved", error: "Failed to save product order" },
                );
              }
            }}
          >
            <table id="example" className="table table-root-floor">
              <thead className="table-root-floor__header">
                <tr className="table-root-floor__header__tr">
                  {headers.map((col) => {
                    if (col.name === "Checkbox") {
                      return (
                        <th
                          key={col.id}
                          className="table-root-floor__header__tr__th table-root-floor__header__tr__th__checkbox"
                        >
                          <div className="d-flex checkbox_">
                            <input
                              type="checkbox"
                              aria-label="Checkbox select all of rows"
                              className="table-root-floor__header__tr__th__checkbox--input"
                              checked={isSelectAllCheckedProducts}
                              onChange={handleSelectAllProducts}
                              disabled={!canEdit}
                            />
                          </div>
                        </th>
                      );
                    }
                    return (
                      <th
                        key={col.id}
                        className={`table-root-floor__header__tr__th table-root-floor__body__tr__td__text ${col.class}`}
                      >
                        <div>{col.name}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <SortableContext items={sorted.map((item) => item.rank)} strategy={verticalListSortingStrategy}>
                <tbody className="table-root-floor__body" ref={tableBodyNode}>
                  {products.length > 0 ? (
                    sorted.map((row) => <DraggableProductRow key={row.productId} row={row} floorId={floorId} />)
                  ) : (
                    <tr>
                      <td colSpan={12} className="no-results--text text-center">
                        Zero results were found based on your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        </div>
      </div>
    </>
  );
};
export default QuoteFloorProducts;
